import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { quizService } from '../services/apiService'

const QuizResults = () => {
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [quiz, setQuiz] = useState(null)
  const [attempts, setAttempts] = useState([])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const fetchData = async () => {
    try {
      const [quizRes, attemptsRes] = await Promise.all([
        quizService.getQuiz(id),
        quizService.getQuizAttempts(id),
      ])

      setQuiz(quizRes.data)
      const attemptsData = attemptsRes.data.results || attemptsRes.data
      setAttempts(Array.isArray(attemptsData) ? attemptsData : [])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStats = () => {
    if (attempts.length === 0) {
      return { total: 0, avgScore: 0, passed: 0, failed: 0, highestScore: 0 }
    }

    const total = attempts.length
    const avgScore = (
      attempts.reduce((sum, a) => sum + a.score, 0) / total
    ).toFixed(1)
    const passed = attempts.filter((a) => a.score >= quiz.passing_score).length
    const failed = total - passed
    const highestScore = Math.max(...attempts.map((a) => a.score))

    return { total, avgScore, passed, failed, highestScore }
  }

  const stats = getStats()

  const getScoreColor = (score) => {
    if (score >= quiz.passing_score) return 'text-green-600'
    if (score >= quiz.passing_score * 0.7) return 'text-yellow-600'
    return 'text-red-600'
  }

  if (loading) {
    return (
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <p className='text-gray-600'>Loading...</p>
      </div>
    )
  }

  if (!quiz) {
    return (
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <p className='text-red-600'>Quiz not found</p>
      </div>
    )
  }

  return (
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      {/* Header */}
      <div className='mb-8'>
        <Link
          to={`/courses/${quiz.course}/manage`}
          className='text-primary-600 hover:text-primary-700 mb-4 inline-block'
        >
          ← Back to Manage Course
        </Link>
        <h1 className='text-3xl font-bold text-gray-900'>{quiz.title}</h1>
        <p className='mt-2 text-gray-600'>Student Quiz Results</p>
      </div>

      {/* Quiz Info */}
      <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div>
            <span className='text-sm text-blue-600 font-medium'>
              Passing Score:
            </span>
            <span className='ml-2 text-blue-900 font-semibold'>
              {quiz.passing_score}%
            </span>
          </div>
          <div>
            <span className='text-sm text-blue-600 font-medium'>
              Time Limit:
            </span>
            <span className='ml-2 text-blue-900 font-semibold'>
              {quiz.time_limit} minutes
            </span>
          </div>
          <div>
            <span className='text-sm text-blue-600 font-medium'>
              Questions:
            </span>
            <span className='ml-2 text-blue-900 font-semibold'>
              {quiz.questions?.length || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className='grid grid-cols-1 md:grid-cols-5 gap-6 mb-8'>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='text-sm font-medium text-gray-600'>
            Total Attempts
          </div>
          <div className='mt-2 text-3xl font-bold text-primary-600'>
            {stats.total}
          </div>
        </div>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='text-sm font-medium text-gray-600'>Average Score</div>
          <div className='mt-2 text-3xl font-bold text-blue-600'>
            {stats.avgScore}%
          </div>
        </div>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='text-sm font-medium text-gray-600'>Passed</div>
          <div className='mt-2 text-3xl font-bold text-green-600'>
            {stats.passed}
          </div>
        </div>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='text-sm font-medium text-gray-600'>Failed</div>
          <div className='mt-2 text-3xl font-bold text-red-600'>
            {stats.failed}
          </div>
        </div>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='text-sm font-medium text-gray-600'>Highest</div>
          <div className='mt-2 text-3xl font-bold text-purple-600'>
            {stats.highestScore}%
          </div>
        </div>
      </div>

      {/* Attempts List */}
      <div className='bg-white rounded-lg shadow overflow-hidden'>
        <div className='px-6 py-4 border-b border-gray-200'>
          <h2 className='text-xl font-semibold text-gray-900'>
            Student Attempts
          </h2>
        </div>

        {attempts.length === 0 ? (
          <div className='px-6 py-12 text-center'>
            <p className='text-gray-500'>No attempts yet</p>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Student
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Score
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Status
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Submitted At
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Time Taken
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {attempts.map((attempt) => {
                  const startTime = new Date(attempt.started_at)
                  const submitTime = new Date(attempt.submitted_at)
                  const timeTaken = Math.round(
                    (submitTime - startTime) / 1000 / 60,
                  ) // minutes

                  return (
                    <tr key={attempt.id} className='hover:bg-gray-50'>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='text-sm font-medium text-gray-900'>
                          {attempt.student?.first_name}{' '}
                          {attempt.student?.last_name}
                        </div>
                        <div className='text-sm text-gray-500'>
                          {attempt.student?.email}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div
                          className={`text-2xl font-bold ${getScoreColor(
                            attempt.score,
                          )}`}
                        >
                          {attempt.score}%
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        {attempt.score >= quiz.passing_score ? (
                          <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800'>
                            ✓ Passed
                          </span>
                        ) : (
                          <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800'>
                            ✗ Failed
                          </span>
                        )}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                        {new Date(attempt.submitted_at).toLocaleString()}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                        {timeTaken} min
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default QuizResults
